/**
 * DeviceStatusSlot — the one place each This Device tab reports its state.
 *
 * TCA-26100804. Every tab puts this in the same position, so an operator looks
 * in one place whichever area they are in. Tone comes from the state and the
 * sentence from the cause; neither is composed here, so a tab cannot invent a
 * status the readiness model did not report.
 *
 * Quiet is a plain line for routine states. The other tones get a tinted panel,
 * because a state that needs attention should not look like one that does not.
 */
import React, { createContext, useContext } from 'react';
import {
  CheckCircle2, Info, OctagonAlert, TriangleAlert,
} from 'lucide-react';

/**
 * When a page-wide problem is showing, every ordinary status slot yields to it.
 * This is a context rather than a prop on each tab because the prop version was
 * forgotten by the very next tab added after it was introduced: a rule that has
 * to be remembered at each call site is not a rule (TCA-26100804).
 */
const StatusOverriddenContext = createContext(false);
export const useServicesUnavailable = () => useContext(StatusOverriddenContext);

export function StatusOverriddenProvider({ overridden = false, children }) {
  return (
    <StatusOverriddenContext.Provider value={Boolean(overridden)}>
      {children}
    </StatusOverriddenContext.Provider>
  );
}

const TONES = Object.freeze({
  green: Object.freeze({
    panel: 'border-emerald-200 bg-emerald-50',
    heading: 'text-emerald-900',
    body: 'text-emerald-800',
    icon: 'text-emerald-600',
    Icon: CheckCircle2,
    label: 'Ready',
  }),
  amber: Object.freeze({
    panel: 'border-amber-200 bg-amber-50',
    heading: 'text-amber-900',
    body: 'text-amber-800',
    icon: 'text-amber-600',
    Icon: TriangleAlert,
    label: 'Needs a look',
  }),
  red: Object.freeze({
    panel: 'border-rose-300 bg-rose-50',
    heading: 'text-rose-900',
    body: 'text-rose-800',
    icon: 'text-rose-600',
    Icon: OctagonAlert,
    label: 'Attention',
  }),
  quiet: Object.freeze({
    panel: '',
    heading: 'text-stone-700',
    body: 'text-stone-500',
    icon: 'text-stone-400',
    Icon: Info,
    label: null,
  }),
});

/**
 * Translate a readiness-presentation tone into a slot tone. The two vocabularies
 * differ in one word — the presentations say `neutral`, the slot says `quiet` —
 * and that map had been written out in three places, one of them rebuilt on
 * every render (TCA-26100804 closing audit).
 */
export function slotTone(tone) {
  return ({ green: 'green', amber: 'amber', red: 'red', neutral: 'quiet' })[tone] || 'quiet';
}

export default function DeviceStatusSlot({
  tone = 'quiet',
  title = null,
  detail = null,
  lines = [],
  testId = 'device-status-slot',
  titleTestId = undefined,
  detailTestId = undefined,
  // The page-wide problem itself opts out; everything else yields to it.
  ignoreOverride = false,
}) {
  const overridden = useContext(StatusOverriddenContext);
  if (overridden && !ignoreOverride) return null;
  const view = TONES[tone] || TONES.quiet;
  const { Icon } = view;
  const body = (lines || []).filter(Boolean);

  if (tone === 'quiet') {
    return (
      <div className="flex items-start gap-2" data-testid={testId} data-tone={tone}>
        <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${view.icon}`} aria-hidden="true" />
        <div className="min-w-0">
          {title && <p className={`text-sm font-medium ${view.heading}`} data-testid={titleTestId}>{title}</p>}
          {detail && <p className={`text-xs mt-0.5 ${view.body}`} data-testid={detailTestId}>{detail}</p>}
          {body.map((line) => (
            <p key={line} className={`text-xs mt-0.5 ${view.body}`}>{line}</p>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border px-4 py-3 ${view.panel}`}
      data-testid={testId}
      data-tone={tone}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${view.icon}`} aria-hidden="true" />
        <div className="min-w-0">
          {view.label && (
            <p className={`text-[11px] font-bold uppercase tracking-wide ${view.icon}`}>
              {view.label}
            </p>
          )}
          {title && <p className={`text-sm font-semibold mt-0.5 ${view.heading}`} data-testid={titleTestId}>{title}</p>}
          {detail && <p className={`text-xs mt-1 ${view.body}`} data-testid={detailTestId}>{detail}</p>}
          {body.map((line) => (
            <p key={line} className={`text-xs mt-1 ${view.body}`}>{line}</p>
          ))}
        </div>
      </div>
    </div>
  );
}
